import { useRef, useState } from "react";
import * as Y from "yjs";
import { newId, type DiagramEdge, type DiagramNode, type DiagramShape } from "@worktools/shared";
import { useBlockProps, useSharedProp } from "../../sync/hooks";
import { useDialogs } from "../../ui/dialogs";
import { IconPlus, IconTrash } from "../icons";

const SHAPES: { value: DiagramShape; label: string }[] = [
  { value: "rect", label: "Rechteck" },
  { value: "round", label: "Abgerundet" },
  { value: "diamond", label: "Raute" },
  { value: "ellipse", label: "Ellipse" },
  { value: "hex", label: "Sechseck" },
];

const FILLS = ["#ffffff", "#f4f3f0", "#dceee9", "#fdf3d0", "#f6dde3", "#dde5f4"];

export function DiagramBlock({ blockId }: { blockId: string }) {
  const props = useBlockProps<{ nodes: DiagramNode[]; edges: DiagramEdge[] }>(blockId);
  const nodesArray = useSharedProp<Y.Array<Y.Map<any>>>(blockId, "nodes");
  const edgesArray = useSharedProp<Y.Array<Y.Map<any>>>(blockId, "edges");
  const surface = useRef<HTMLDivElement>(null);
  const dialogs = useDialogs();
  const [selected, setSelected] = useState<string | null>(null);
  const [linkFrom, setLinkFrom] = useState<string | null>(null);

  const nodes = props.nodes ?? [];
  const edges = props.edges ?? [];
  const active = nodes.find((node) => node.id === selected) ?? null;

  const addNode = () => {
    if (!nodesArray) return;
    const node = new Y.Map<any>();
    const id = newId("n");
    node.set("id", id);
    node.set("x", 40 + nodes.length * 26);
    node.set("y", 40 + (nodes.length % 4) * 30);
    node.set("w", 130);
    node.set("h", 56);
    node.set("label", `Schritt ${nodes.length + 1}`);
    node.set("shape", "round");
    node.set("fill", "#ffffff");
    nodesArray.push([node]);
    setSelected(id);
  };

  const updateNode = (id: string, patch: Partial<DiagramNode>) => {
    const index = nodes.findIndex((node) => node.id === id);
    const node = nodesArray?.get(index);
    node?.doc?.transact(() => {
      for (const [key, value] of Object.entries(patch)) node.set(key, value);
    });
  };

  const removeNode = (id: string) => {
    nodesArray?.doc?.transact(() => {
      const index = nodes.findIndex((node) => node.id === id);
      if (index >= 0) nodesArray.delete(index, 1);
      for (let i = edges.length - 1; i >= 0; i -= 1) {
        if (edges[i].from === id || edges[i].to === id) edgesArray?.delete(i, 1);
      }
    });
    setSelected(null);
  };

  const connect = (toId: string) => {
    if (!linkFrom || linkFrom === toId || !edgesArray) return setLinkFrom(null);
    const edge = new Y.Map<any>();
    edge.set("id", newId("e"));
    edge.set("from", linkFrom);
    edge.set("to", toId);
    edgesArray.push([edge]);
    setLinkFrom(null);
  };

  const dragNode = (event: React.PointerEvent, node: DiagramNode) => {
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    const origin = { x: event.clientX, y: event.clientY, nodeX: node.x, nodeY: node.y };

    const move = (moveEvent: PointerEvent) => {
      const scale = surface.current ? 1 : 1;
      updateNode(node.id, {
        x: Math.max(0, origin.nodeX + (moveEvent.clientX - origin.x) / scale),
        y: Math.max(0, origin.nodeY + (moveEvent.clientY - origin.y) / scale),
      });
    };
    const stop = () => {
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", stop);
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", stop);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%" }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <button className="pill pill--solid" onClick={addNode}>
          <IconPlus size={12} /> Knoten
        </button>
        <button
          className={`pill${linkFrom ? " pill--solid" : ""}`}
          onClick={() => setLinkFrom(linkFrom ? null : selected)}
          disabled={!selected && !linkFrom}
        >
          {linkFrom ? "Ziel wählen…" : "Verbinden"}
        </button>
        {active && (
          <>
            <select value={active.shape} onChange={(event) => updateNode(active.id, { shape: event.target.value as DiagramShape })}>
              {SHAPES.map((shape) => (
                <option key={shape.value} value={shape.value}>
                  {shape.label}
                </option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 4 }}>
              {FILLS.map((fill) => (
                <button
                  key={fill}
                  className={`swatch${active.fill === fill ? " swatch--active" : ""}`}
                  style={{ background: fill }}
                  onClick={() => updateNode(active.id, { fill })}
                />
              ))}
            </div>
            <button className="row-action" onClick={() => removeNode(active.id)}>
              <IconTrash size={13} />
            </button>
          </>
        )}
      </div>

      <div
        ref={surface}
        className="diagram"
        onPointerDown={() => {
          setSelected(null);
          setLinkFrom(null);
        }}
      >
        <svg className="diagram__edges">
          {edges.map((edge) => {
            const from = nodes.find((node) => node.id === edge.from);
            const to = nodes.find((node) => node.id === edge.to);
            if (!from || !to) return null;
            const start = { x: from.x + from.w / 2, y: from.y + from.h / 2 };
            const end = { x: to.x + to.w / 2, y: to.y + to.h / 2 };
            return (
              <g key={edge.id}>
                <line
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                  stroke="var(--ink-muted)"
                  strokeWidth={1.4}
                  strokeDasharray={edge.dashed ? "5 4" : undefined}
                  markerEnd="url(#arrow)"
                />
              </g>
            );
          })}
          <defs>
            <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0 0 L10 5 L0 10 z" fill="var(--ink-muted)" />
            </marker>
          </defs>
        </svg>

        {nodes.map((node) => (
          <div
            key={node.id}
            className={`dnode dnode--${node.shape}${selected === node.id ? " dnode--selected" : ""}`}
            style={{ left: node.x, top: node.y, width: node.w, height: node.h, background: node.fill }}
            onPointerDown={(event) => {
              event.stopPropagation();
              if (linkFrom) return connect(node.id);
              setSelected(node.id);
              dragNode(event, node);
            }}
            onDoubleClick={async (event) => {
              event.stopPropagation();
              const label = await dialogs.prompt({
                title: "Beschriftung",
                label: "Text des Knotens",
                value: node.label,
              });
              if (label !== null) updateNode(node.id, { label });
            }}
          >
            <span>{node.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
